import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Input,
  Select,
  useToast,
  useBreakpointValue
} from '@chakra-ui/react';
import ReactSlider from 'react-slider';
import { FilterType, FilterSlope } from '../utils/mobileAudioContextManager';

interface FilterPreset {
  label: string;
  type: FilterType;
  frequency: number;
  frequencyLow?: number;
  frequencyHigh?: number;
}

const FILTER_PRESETS: FilterPreset[] = [
  { label: 'None', type: 'off', frequency: 1000 },
  { label: 'Sub', type: 'lowpass', frequency: 60 },
  { label: 'Bass', type: 'bandpass', frequency: 130, frequencyLow: 60, frequencyHigh: 200 },
  { label: 'Low Mid', type: 'bandpass', frequency: 350, frequencyLow: 200, frequencyHigh: 500 },
  { label: 'Mid', type: 'bandpass', frequency: 1250, frequencyLow: 500, frequencyHigh: 2000 },
  { label: 'High Mid', type: 'bandpass', frequency: 3500, frequencyLow: 2000, frequencyHigh: 5000 },
  { label: 'High', type: 'bandpass', frequency: 7500, frequencyLow: 5000, frequencyHigh: 10000 },
  { label: 'Air', type: 'highpass', frequency: 10000 },
];

interface MasterFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterType: FilterType;
  filterFrequency: number;
  filterQ: number;
  filterSlope: FilterSlope;
  filterFrequencyLow: number;
  filterFrequencyHigh: number;
  filterInputGain: number;
  onFilterChange: (type: FilterType, frequency: number, q: number, slope?: FilterSlope, frequencyLow?: number, frequencyHigh?: number, inputGain?: number) => Promise<void>;
  onReset: () => Promise<void>;
}

// Logarithmic scale conversion for frequency slider
const logToLinear = (value: number, min: number, max: number): number => {
  const minLog = Math.log10(min);
  const maxLog = Math.log10(max);
  const logValue = minLog + (maxLog - minLog) * value;
  return Math.pow(10, logValue);
};

const linearToLog = (value: number, min: number, max: number): number => {
  const minLog = Math.log10(min);
  const maxLog = Math.log10(max);
  const valueLog = Math.log10(value);
  return (valueLog - minLog) / (maxLog - minLog);
};

const MasterFilterModal: React.FC<MasterFilterModalProps> = ({
  isOpen,
  onClose,
  filterType,
  filterFrequency,
  filterQ,
  filterSlope,
  filterFrequencyLow,
  filterFrequencyHigh,
  filterInputGain,
  onFilterChange,
  onReset
}) => {
  const [localType, setLocalType] = useState<FilterType>(filterType);
  const [localFrequency, setLocalFrequency] = useState<number>(filterFrequency);
  const [localSlope, setLocalSlope] = useState<FilterSlope>(filterSlope);
  const [localFrequencyLow, setLocalFrequencyLow] = useState<number>(filterFrequencyLow);
  const [localFrequencyHigh, setLocalFrequencyHigh] = useState<number>(filterFrequencyHigh);
  const [localInputGain, setLocalInputGain] = useState<number>(filterInputGain);
  const [lastActiveType, setLastActiveType] = useState<FilterType>(filterType === 'off' ? 'highpass' : filterType);
  
  // Track slider values for visual feedback (without applying filter)
  const [sliderFrequency, setSliderFrequency] = useState<number>(filterFrequency);
  const [sliderFrequencyLow, setSliderFrequencyLow] = useState<number>(filterFrequencyLow);
  const [sliderFrequencyHigh, setSliderFrequencyHigh] = useState<number>(filterFrequencyHigh);
  
  // Q is always Linkwitz-Riley (0.5) for minimal resonance - no user control
  const LINKWITZ_RILEY_Q = 0.5;
  
  // Track input values as strings to allow free typing
  const [inputFrequency, setInputFrequency] = useState<string>(filterFrequency.toString());
  const [inputFrequencyLow, setInputFrequencyLow] = useState<string>(filterFrequencyLow.toString());
  const [inputFrequencyHigh, setInputFrequencyHigh] = useState<string>(filterFrequencyHigh.toString());
  
  const toast = useToast();
  const isDesktop = useBreakpointValue({ base: false, md: true });

  // Update local state when props change
  useEffect(() => {
    setLocalType(filterType);
    setLocalFrequency(filterFrequency);
    setLocalSlope(filterSlope);
    setLocalFrequencyLow(filterFrequencyLow);
    setLocalFrequencyHigh(filterFrequencyHigh);
    setLocalInputGain(filterInputGain);
    setSliderFrequency(filterFrequency);
    setSliderFrequencyLow(filterFrequencyLow);
    setSliderFrequencyHigh(filterFrequencyHigh);
    setInputFrequency(filterFrequency.toString());
    setInputFrequencyLow(filterFrequencyLow.toString());
    setInputFrequencyHigh(filterFrequencyHigh.toString());
    if (filterType !== 'off') {
      setLastActiveType(filterType);
    }
  }, [filterType, filterFrequency, filterSlope, filterFrequencyLow, filterFrequencyHigh, filterInputGain, isOpen]);

  // Apply filter changes
  const applyFilter = useCallback(async (
    type: FilterType, 
    freq: number, 
    q: number, 
    slope: FilterSlope = 12, 
    freqLow: number = 500, 
    freqHigh: number = 2000,
    inputGain: number = 1.0
  ) => {
    try {
      await onFilterChange(type, freq, q, slope, freqLow, freqHigh, inputGain);
    } catch (error) {
      console.error('Failed to apply filter:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply filter settings',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [onFilterChange, toast]);

  // Handle filter type change
  const handleTypeChange = async (newType: FilterType) => {
    setLocalType(newType);
    if (newType === 'off') {
      await applyFilter('off', 1000, LINKWITZ_RILEY_Q, 12, 500, 2000, localInputGain);
    } else {
      setLastActiveType(newType);
      if (newType === 'bandpass') {
        await applyFilter(newType, (localFrequencyLow + localFrequencyHigh) / 2, LINKWITZ_RILEY_Q, localSlope, localFrequencyLow, localFrequencyHigh, localInputGain);
      } else {
        await applyFilter(newType, localFrequency, LINKWITZ_RILEY_Q, localSlope, 500, 2000, localInputGain);
      }
    }
  };

  // Handle frequency change (for HP/LP)
  const handleFrequencyChange = async (value: number) => {
    const clampedValue = Math.max(20, Math.min(20000, value));
    const roundedValue = Math.round(clampedValue / 10) * 10;
    setLocalFrequency(roundedValue);
    setInputFrequency(roundedValue.toString());
    await applyFilter(localType, roundedValue, LINKWITZ_RILEY_Q, localSlope, localFrequencyLow, localFrequencyHigh, localInputGain);
  };

  // Handle frequency low change (for BP)
  const handleFrequencyLowChange = async (value: number) => {
    const clampedValue = Math.max(20, Math.min(20000, value));
    const roundedValue = Math.round(clampedValue / 10) * 10;
    // If the new low value is >= high value, adjust high to be 10Hz above low
    let finalLow = roundedValue;
    let finalHigh = localFrequencyHigh;
    if (finalLow >= finalHigh) {
      finalHigh = Math.min(20000, finalLow + 10);
      setLocalFrequencyHigh(finalHigh);
      setSliderFrequencyHigh(finalHigh);
      setInputFrequencyHigh(finalHigh.toString());
    }
    setLocalFrequencyLow(finalLow);
    setInputFrequencyLow(finalLow.toString());
    await applyFilter(localType, (finalLow + finalHigh) / 2, LINKWITZ_RILEY_Q, localSlope, finalLow, finalHigh, localInputGain);
  };

  // Handle frequency high change (for BP)
  const handleFrequencyHighChange = async (value: number) => {
    const clampedValue = Math.max(20, Math.min(20000, value));
    const roundedValue = Math.round(clampedValue / 10) * 10;
    // If the new high value is <= low value, adjust low to be 10Hz below high
    let finalLow = localFrequencyLow;
    let finalHigh = roundedValue;
    if (finalHigh <= finalLow) {
      finalLow = Math.max(20, finalHigh - 10);
      setLocalFrequencyLow(finalLow);
      setSliderFrequencyLow(finalLow);
      setInputFrequencyLow(finalLow.toString());
    }
    setLocalFrequencyHigh(finalHigh);
    setInputFrequencyHigh(finalHigh.toString());
    await applyFilter(localType, (finalLow + finalHigh) / 2, LINKWITZ_RILEY_Q, localSlope, finalLow, finalHigh, localInputGain);
  };


  // Helper function to parse frequency input with "k" shorthand (e.g., "2.5k" -> 2500)
  const parseFrequencyInput = (input: string): number | null => {
    if (!input || input.trim() === '') {
      return null;
    }
    
    const trimmed = input.trim().toLowerCase();
    const hasK = trimmed.endsWith('k');
    
    if (hasK) {
      const numPart = trimmed.slice(0, -1).trim();
      const numValue = parseFloat(numPart);
      if (!isNaN(numValue)) {
        return numValue * 1000;
      }
    } else {
      const numValue = parseFloat(trimmed);
      if (!isNaN(numValue)) {
        return numValue;
      }
    }
    
    return null;
  };

  // Handle frequency input change (update string state only)
  const handleFrequencyInputChange = (valueAsString: string, valueAsNumber: number) => {
    setInputFrequency(valueAsString);
  };

  // Handle frequency input commit (on blur or Enter)
  const handleFrequencyInputCommit = async () => {
    const numValue = parseFrequencyInput(inputFrequency);
    if (numValue !== null) {
      await handleFrequencyChange(numValue);
    } else {
      // Reset to current value if invalid
      setInputFrequency(localFrequency.toString());
    }
  };

  // Handle frequency low input change (update string state only)
  const handleFrequencyLowInputChange = (valueAsString: string, valueAsNumber: number) => {
    setInputFrequencyLow(valueAsString);
  };

  // Handle frequency low input commit (on blur or Enter)
  const handleFrequencyLowInputCommit = async () => {
    const numValue = parseFrequencyInput(inputFrequencyLow);
    if (numValue !== null) {
      await handleFrequencyLowChange(numValue);
    } else {
      // Reset to current value if invalid
      setInputFrequencyLow(localFrequencyLow.toString());
    }
  };

  // Handle frequency high input change (update string state only)
  const handleFrequencyHighInputChange = (valueAsString: string, valueAsNumber: number) => {
    setInputFrequencyHigh(valueAsString);
  };

  // Handle frequency high input commit (on blur or Enter)
  const handleFrequencyHighInputCommit = async () => {
    const numValue = parseFrequencyInput(inputFrequencyHigh);
    if (numValue !== null) {
      await handleFrequencyHighChange(numValue);
    } else {
      // Reset to current value if invalid
      setInputFrequencyHigh(localFrequencyHigh.toString());
    }
  };

  // Handle slope change (for HP/LP/BP)
  const handleSlopeChange = async (value: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row onClick from firing
    }
    const newSlope = parseInt(value) as FilterSlope;
    setLocalSlope(newSlope);
    if (localType === 'bandpass') {
      await applyFilter(localType, (localFrequencyLow + localFrequencyHigh) / 2, LINKWITZ_RILEY_Q, newSlope, localFrequencyLow, localFrequencyHigh, localInputGain);
    } else {
      await applyFilter(localType, localFrequency, LINKWITZ_RILEY_Q, newSlope, localFrequencyLow, localFrequencyHigh, localInputGain);
    }
  };

  // Handle preset selection
  const handlePresetClick = useCallback(async (preset: FilterPreset) => {
    setLocalType(preset.type);
    const roundedFreq = Math.round(preset.frequency / 10) * 10;
    setLocalFrequency(roundedFreq);
    setInputFrequency(roundedFreq.toString());
    setLocalSlope(12);
    if (preset.type === 'off') {
      await applyFilter('off', 1000, LINKWITZ_RILEY_Q, 12, 500, 2000, localInputGain);
    } else if (preset.type === 'bandpass') {
      // Use preset frequency range if provided, otherwise calculate a range around the frequency
      const freqLow = preset.frequencyLow ?? Math.max(20, preset.frequency - preset.frequency * 0.5);
      const freqHigh = preset.frequencyHigh ?? Math.min(20000, preset.frequency + preset.frequency * 0.5);
      const roundedLow = Math.round(freqLow / 10) * 10;
      const roundedHigh = Math.round(freqHigh / 10) * 10;
      setLocalFrequencyLow(roundedLow);
      setLocalFrequencyHigh(roundedHigh);
      setInputFrequencyLow(roundedLow.toString());
      setInputFrequencyHigh(roundedHigh.toString());
      await applyFilter(preset.type, roundedFreq, LINKWITZ_RILEY_Q, 12, roundedLow, roundedHigh, localInputGain);
    } else {
      await applyFilter(preset.type, roundedFreq, LINKWITZ_RILEY_Q, 12, 500, 2000, localInputGain);
    }
  }, [applyFilter, localInputGain]);

  // Handle number key presses to select presets
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip hotkeys when user is editing text
      if (e.target instanceof HTMLInputElement || 
          e.target instanceof HTMLTextAreaElement ||
          (e.target instanceof HTMLElement && e.target.contentEditable === 'true')) {
        return;
      }

      // Handle number keys 0-8 for preset selection
      const numKey = parseInt(e.key);
      if (numKey >= 0 && numKey < FILTER_PRESETS.length && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other handlers on window from running
        const preset = FILTER_PRESETS[numKey];
        if (preset) {
          handlePresetClick(preset);
        }
      }
    };

    // Use capture phase to ensure this handler runs before other window-level handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, handlePresetClick]);

  // Convert frequency to slider value (0-100) - use slider state for visual feedback
  const frequencySliderValue = linearToLog(sliderFrequency, 20, 20000) * 100;
  const frequencyLowSliderValue = linearToLog(sliderFrequencyLow, 20, 20000) * 100;
  const frequencyHighSliderValue = linearToLog(sliderFrequencyHigh, 20, 20000) * 100;
  const frequencyRangeSliderValue: [number, number] = [frequencyLowSliderValue, frequencyHighSliderValue];

  // Handle slider change (apply filter in real-time - no zipper noise with parameter updates)
  const handleFrequencySliderChange = async (value: number | number[]) => {
    const numValue = Array.isArray(value) ? value[0] : value;
    const freq = logToLinear(numValue / 100, 20, 20000);
    const roundedFreq = Math.round(Math.max(20, Math.min(20000, freq)) / 10) * 10;
    setSliderFrequency(roundedFreq);
    setLocalFrequency(roundedFreq);
    // Update input field in real-time for visual feedback
    setInputFrequency(roundedFreq.toString());
    // Apply filter in real-time (will reuse nodes if only frequency changed)
    await applyFilter(localType, roundedFreq, LINKWITZ_RILEY_Q, localSlope, localFrequencyLow, localFrequencyHigh, localInputGain);
  };

  // Handle range slider change (apply filter in real-time - no zipper noise with parameter updates)
  const handleFrequencyRangeSliderChange = async (values: number | number[]) => {
    const arrayValues = Array.isArray(values) ? values : [values];
    const [lowValue, highValue] = arrayValues;
    const freqLow = logToLinear(lowValue / 100, 20, 20000);
    const freqHigh = logToLinear(highValue / 100, 20, 20000);
    // Round to nearest 10Hz
    let finalLow = Math.round(Math.max(20, Math.min(20000, freqLow)) / 10) * 10;
    let finalHigh = Math.round(Math.max(20, Math.min(20000, freqHigh)) / 10) * 10;
    
    // Ensure low < high
    if (finalLow >= finalHigh) {
      const mid = Math.round((finalLow + finalHigh) / 2 / 10) * 10;
      finalLow = Math.max(20, mid - 10);
      finalHigh = Math.min(20000, mid + 10);
    }
    
    setSliderFrequencyLow(finalLow);
    setSliderFrequencyHigh(finalHigh);
    setLocalFrequencyLow(finalLow);
    setLocalFrequencyHigh(finalHigh);
    // Update input fields in real-time for visual feedback
    setInputFrequencyLow(finalLow.toString());
    setInputFrequencyHigh(finalHigh.toString());
    // Apply filter in real-time (will reuse nodes if only frequency changed)
    await applyFilter(localType, (finalLow + finalHigh) / 2, LINKWITZ_RILEY_Q, localSlope, finalLow, finalHigh, localInputGain);
  };

  // Handle range slider change end (no-op now, filter already applied in real-time)
  const handleFrequencyRangeSliderChangeEnd = useCallback(async (values: number | number[]) => {
    // Filter is already applied in handleFrequencyRangeSliderChange
    // This is kept for compatibility but does nothing
  }, []);

  // Handle track drag to move both handles together
  const handleTrackDrag = useCallback((e: React.MouseEvent | React.TouchEvent, trackIndex: number) => {
    // Only allow dragging on the middle track (between handles)
    if (trackIndex !== 1) return;

    e.preventDefault();
    e.stopPropagation();

    const trackElement = e.currentTarget as HTMLElement;
    const sliderContainer = trackElement.closest('.range-slider-container') as HTMLElement;
    if (!sliderContainer) return;

    const rect = sliderContainer.getBoundingClientRect();
    const isTouch = 'touches' in e;
    const clientX = isTouch ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
    
    const startX = clientX;
    const startLow = frequencyLowSliderValue;
    const startHigh = frequencyHighSliderValue;
    const sliderWidth = rect.width;

    // Track the current values during drag
    let currentLow = startLow;
    let currentHigh = startHigh;

    const handleMouseMove = async (moveEvent: Event) => {
      const moveClientX = 'touches' in moveEvent ? (moveEvent as TouchEvent).touches[0].clientX : (moveEvent as MouseEvent).clientX;
      const deltaX = moveClientX - startX;
      const deltaPercent = (deltaX / sliderWidth) * 100;
      
      let newLow = startLow + deltaPercent;
      let newHigh = startHigh + deltaPercent;
      
      // Clamp to bounds
      if (newLow < 0) {
        const diff = 0 - newLow;
        newLow = 0;
        newHigh = Math.min(100, startHigh + deltaPercent - diff);
      }
      if (newHigh > 100) {
        const diff = newHigh - 100;
        newHigh = 100;
        newLow = Math.max(0, startLow + deltaPercent - diff);
      }
      
      // Store current values
      currentLow = newLow;
      currentHigh = newHigh;
      
      // Update slider state and apply filter in real-time
      const freqLow = logToLinear(newLow / 100, 20, 20000);
      const freqHigh = logToLinear(newHigh / 100, 20, 20000);
      // Round to nearest 10Hz
      let finalLow = Math.round(Math.max(20, Math.min(20000, freqLow)) / 10) * 10;
      let finalHigh = Math.round(Math.max(20, Math.min(20000, freqHigh)) / 10) * 10;
      
      // Ensure low < high
      if (finalLow >= finalHigh) {
        const mid = Math.round((finalLow + finalHigh) / 2 / 10) * 10;
        finalLow = Math.max(20, mid - 10);
        finalHigh = Math.min(20000, mid + 10);
      }
      
      setSliderFrequencyLow(finalLow);
      setSliderFrequencyHigh(finalHigh);
      setLocalFrequencyLow(finalLow);
      setLocalFrequencyHigh(finalHigh);
      // Update input fields in real-time for visual feedback
      setInputFrequencyLow(finalLow.toString());
      setInputFrequencyHigh(finalHigh.toString());
      // Apply filter in real-time (will reuse nodes if only frequency changed)
      await applyFilter(localType, (finalLow + finalHigh) / 2, LINKWITZ_RILEY_Q, localSlope, finalLow, finalHigh, localInputGain);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove as EventListener);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove as EventListener);
      document.removeEventListener('touchend', handleMouseUp);
      
      // Filter already applied in handleMouseMove, no need to apply again
    };

    document.addEventListener('mousemove', handleMouseMove as EventListener);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove as EventListener);
    document.addEventListener('touchend', handleMouseUp);
  }, [frequencyLowSliderValue, frequencyHighSliderValue, localType, localSlope, localInputGain, applyFilter]);

  // Handle slider change end (no-op now, filter already applied in real-time)
  const handleFrequencySliderChangeEnd = async (value: number | number[]) => {
    // Filter is already applied in handleFrequencySliderChange
    // This is kept for compatibility but does nothing
  };

  // Handle input gain change
  const handleInputGainChange = async (value: number) => {
    const clampedValue = Math.max(0, Math.min(2, value));
    setLocalInputGain(clampedValue);
    await applyFilter(localType, localType === 'bandpass' ? (localFrequencyLow + localFrequencyHigh) / 2 : localFrequency, LINKWITZ_RILEY_Q, localSlope, localFrequencyLow, localFrequencyHigh, clampedValue);
  };

  const formatFrequency = (freq: number): string => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(1)}kHz`;
    }
    return `${Math.round(freq)}Hz`;
  };

  // Check if current filter settings match a preset
  const getMatchingPreset = (): FilterPreset | null => {
    // Presets use slope 12 (input gain is not changed by presets, so we don't check it)
    if (localSlope !== 12) {
      return null;
    }

    // Check each preset
    for (const preset of FILTER_PRESETS) {
      if (localType !== preset.type) {
        continue;
      }

      if (preset.type === 'off') {
        // For 'off', just match the type
        return preset;
      } else if (preset.type === 'bandpass') {
        // For bandpass, check frequency range
        const presetLow = preset.frequencyLow ?? Math.max(20, preset.frequency - preset.frequency * 0.5);
        const presetHigh = preset.frequencyHigh ?? Math.min(20000, preset.frequency + preset.frequency * 0.5);
        const roundedPresetLow = Math.round(presetLow / 10) * 10;
        const roundedPresetHigh = Math.round(presetHigh / 10) * 10;
        
        if (localFrequencyLow === roundedPresetLow && localFrequencyHigh === roundedPresetHigh) {
          return preset;
        }
      } else {
        // For HP/LP, check frequency
        const roundedPresetFreq = Math.round(preset.frequency / 10) * 10;
        if (localFrequency === roundedPresetFreq) {
          return preset;
        }
      }
    }

    return null;
  };

  const activePreset = getMatchingPreset();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="gray.800" maxW="2xl">
        <ModalHeader color="gray.100">Master Filter Controls</ModalHeader>
        <ModalCloseButton color="gray.300" />
        
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Presets at top */}
            <Box>
              <VStack spacing={2} align="center">
                <FormLabel color="gray.200" fontSize="sm" fontWeight="medium" mb={0} textAlign="center">
                  Presets
                </FormLabel>
                <HStack spacing={{ base: 0, md: 2 }} wrap="wrap" justify={{ base: "space-between", md: "center" }} w={{ base: "100%", md: "auto" }}>
                  {FILTER_PRESETS.map((preset, index) => {
                    const isActive = activePreset?.label === preset.label;
                    return (
                      <VStack key={preset.label} spacing={0} align="center">
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => handlePresetClick(preset)}
                          color="gray.200"
                          bg="transparent"
                          borderColor={isActive ? "accent.500" : "gray.600"}
                          boxShadow={isActive ? "0 0 20px rgba(255, 152, 0, 0.15)" : "none"}
                          _hover={{ 
                            bg: "gray.700", 
                            borderColor: isActive ? "accent.500" : "blue.400" 
                          }}
                          px={{ base: "3px", md: undefined }}
                          whiteSpace={{ base: "nowrap", md: undefined }}
                        >
                          {preset.label}
                        </Button>
                        {isDesktop && (
                          <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight={1}>
                            {index}
                          </Text>
                        )}
                      </VStack>
                    );
                  })}
                </HStack>
              </VStack>
            </Box>

            {/* Unified Filter Controls */}
            <Box
              p={4}
              borderRadius="md"
              bg="gray.700"
              borderWidth={1}
              borderColor="gray.600"
            >
                <VStack spacing={3} align="stretch">
                {/* Filter Type Selector */}
                <Box>
                  <HStack spacing={4} align="center" h="32px">
                    <FormLabel color="gray.200" fontSize="sm" fontWeight="medium" mb={0} h="32px" display="flex" alignItems="center">
                      Filter Type
                    </FormLabel>
                    <HStack spacing={2} align="center" h="32px">
                      {/* None button */}
                      <Button
                        size="sm"
                        variant={localType === 'off' ? "solid" : "outline"}
                        colorScheme="blue"
                        onClick={() => handleTypeChange('off')}
                        color={localType === 'off' ? "white" : "gray.200"}
                        bg={localType === 'off' ? "blue.500" : "transparent"}
                        borderColor={localType === 'off' ? "blue.500" : "gray.600"}
                        h="32px"
                        _hover={{
                          bg: localType === 'off' ? "blue.600" : "gray.700",
                          borderColor: "blue.400"
                        }}
                      >
                        None
                      </Button>
                      {(['lowpass', 'bandpass', 'highpass'] as FilterType[]).map((type) => (
                        <Button
                          key={type}
                          size="sm"
                          variant={localType === type ? "solid" : "outline"}
                          colorScheme="blue"
                          onClick={() => handleTypeChange(type)}
                          color={localType === type ? "white" : "gray.200"}
                          bg={localType === type ? "blue.500" : "transparent"}
                          borderColor={localType === type ? "blue.500" : "gray.600"}
                          h="32px"
                          _hover={{
                            bg: localType === type ? "blue.600" : "gray.700",
                            borderColor: "blue.400"
                          }}
                        >
                          {type === 'highpass' ? 'High' : type === 'lowpass' ? 'Low' : 'Band'}
                        </Button>
                      ))}
                    </HStack>
                  </HStack>
                  </Box>
                  {/* Frequency Controls Container - Fixed height to prevent position shifts */}
                  <Box position="relative" minH="70px" mb={-3}>
                    {/* Frequency Control - HP/LP */}
                    <FormControl 
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      style={{ visibility: localType !== 'off' && (localType === 'highpass' || localType === 'lowpass') ? 'visible' : 'hidden' }}
                    >
                      <FormLabel color="gray.200" fontSize="sm" mb={2}>
                        Frequency: {formatFrequency(sliderFrequency)}
                      </FormLabel>
                      <HStack spacing={4} align="center">
                        <Box flex={1} px={2}>
                          <ReactSlider
                            value={frequencySliderValue}
                            onChange={handleFrequencySliderChange}
                            onAfterChange={handleFrequencySliderChangeEnd}
                            min={0}
                            max={100}
                            step={0.1}
                            renderThumb={(props: React.HTMLAttributes<HTMLDivElement>, state: { index: number; value: number | number[]; valueNow: number }) => (
                              <div {...props} style={{
                                ...props.style,
                                height: '20px',
                                width: '20px',
                                backgroundColor: '#3182ce',
                                borderRadius: '50%',
                                cursor: 'grab',
                                border: '2px solid #2c5282',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                outline: 'none',
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: '#90cdf4',
                                  borderRadius: '50%'
                                }} />
                              </div>
                            )}
                            renderTrack={(props: React.HTMLAttributes<HTMLDivElement>, state: { index: number; value: number | number[] }) => {
                              const { index } = state;
                              // For single-value slider: index 0 is before thumb, index 1 is after thumb
                              // For high pass: filled portion is after thumb (right side)
                              // For low pass: filled portion is before thumb (left side)
                              const isHighPass = localType === 'highpass';
                              const isFilled = isHighPass ? index === 1 : index === 0;
                              return (
                                <div
                                  {...props}
                                  style={{
                                    ...props.style,
                                    height: '8px',
                                    backgroundColor: isFilled ? '#3182ce' : '#4a5568',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    top: '50%',
                                    transform: 'translateY(-50%)'
                                  }}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Input
                          value={inputFrequency}
                          onChange={(e) => setInputFrequency(e.target.value)}
                          onBlur={handleFrequencyInputCommit}
                          size="sm"
                          w="100px"
                          bg="gray.600" 
                          borderColor="gray.500" 
                          color="gray.100"
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="Hz or k"
                        />
                      </HStack>
                    </FormControl>

                    {/* Frequency Range Control - BP */}
                    <FormControl 
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      style={{ visibility: localType !== 'off' && localType === 'bandpass' ? 'visible' : 'hidden' }}
                    >
                      <FormLabel color="gray.200" fontSize="sm" mb={2}>
                        Frequency Range: {formatFrequency(sliderFrequencyLow)} - {formatFrequency(sliderFrequencyHigh)}
                      </FormLabel>
                      <HStack spacing={4} align="center">
                        <Box flex={1} px={2} className="range-slider-container">
                          <ReactSlider
                            value={frequencyRangeSliderValue}
                            onChange={handleFrequencyRangeSliderChange}
                            onAfterChange={handleFrequencyRangeSliderChangeEnd}
                            min={0}
                            max={100}
                            step={0.1}
                            className="horizontal-slider"
                            renderThumb={(props: React.HTMLAttributes<HTMLDivElement>, state: { index: number; value: number | number[]; valueNow: number }) => (
                              <div {...props} style={{
                                ...props.style,
                                height: '20px',
                                width: '20px',
                                backgroundColor: '#3182ce',
                                borderRadius: '50%',
                                cursor: 'grab',
                                border: '2px solid #2c5282',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                outline: 'none',
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: '#90cdf4',
                                  borderRadius: '50%'
                                }} />
                              </div>
                            )}
                            renderTrack={(props: React.HTMLAttributes<HTMLDivElement>, state: { index: number; value: number | number[] }) => {
                              const { index } = state;
                              // Track 0 is before first thumb, track 1 is between thumbs, track 2 is after second thumb
                              return (
                                <div
                                  {...props}
                                  style={{
                                    ...props.style,
                                    height: '8px',
                                    backgroundColor: index === 1 ? '#3182ce' : '#4a5568',
                                    borderRadius: '4px',
                                    cursor: index === 1 ? 'grab' : 'pointer',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    userSelect: 'none'
                                  }}
                                  onMouseDown={index === 1 ? (e) => handleTrackDrag(e, index) : undefined}
                                  onTouchStart={index === 1 ? (e) => handleTrackDrag(e, index) : undefined}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Input
                          value={inputFrequencyLow}
                          onChange={(e) => setInputFrequencyLow(e.target.value)}
                          onBlur={handleFrequencyLowInputCommit}
                          size="sm"
                          w="60px"
                          bg="gray.600" 
                          borderColor="gray.500" 
                          color="gray.100"
                          fontSize="sm"
                          px={1}
                          textAlign="center"
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="Hz or k"
                        />
                        <Input
                          value={inputFrequencyHigh}
                          onChange={(e) => setInputFrequencyHigh(e.target.value)}
                          onBlur={handleFrequencyHighInputCommit}
                          size="sm"
                          w="60px"
                          bg="gray.600" 
                          borderColor="gray.500" 
                          color="gray.100"
                          fontSize="sm"
                          px={1}
                          textAlign="center"
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="Hz or k"
                        />
                      </HStack>
                    </FormControl>
                  </Box>

                  {/* Slope Control */}
                  <FormControl style={{ visibility: localType !== 'off' ? 'visible' : 'hidden' }}>
                    <HStack spacing={4} align="center">
                      <FormLabel color="gray.200" fontSize="sm" mb={0} minW="40px">Slope</FormLabel>
                      <HStack spacing={2}>
                        {([12, 24] as FilterSlope[]).map((slope) => (
                          <Button
                            key={slope}
                            size="sm"
                            variant={localSlope === slope ? "solid" : "outline"}
                            colorScheme="blue"
                            onClick={(e) => handleSlopeChange(slope.toString(), e)}
                            color={localSlope === slope ? "white" : "gray.200"}
                            bg={localSlope === slope ? "blue.500" : "transparent"}
                            borderColor={localSlope === slope ? "blue.500" : "gray.600"}
                            _hover={{
                              bg: localSlope === slope ? "blue.600" : "gray.700",
                              borderColor: "blue.400"
                            }}
                          >
                            {slope}dB
                          </Button>
                        ))}
                      </HStack>
                    </HStack>
                  </FormControl>
                </VStack>
              </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            onClick={onClose}
            colorScheme="blue"
            bg="blue.500"
            _hover={{ bg: "blue.600" }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MasterFilterModal;
